import BadgeCheck from '@/assets/badge-check.svg?react';
import Confetti from '@/assets/confetti.svg?react';
import Flower from '@/assets/flower.svg?react';
import Phone from '@/assets/phone.svg?react';
import Question from '@/assets/question.svg?react';
import Warning from '@/assets/warning.svg?react';
import Button, { ButtonProps, ButtonRightArrow } from '@/components/ui/Button';
import { PRODUCT } from '@/consts';
import getHelpfindPhone from '@/helpers/getHelpfindPhone';
import cn from '@/utils/cn';

import Avatar from './Avatar';
import { useLingui } from '@lingui/react';

interface Props extends React.ComponentPropsWithoutRef<'div'> {
  consultant?: {
    name: string;
    jobDescription: string;
    image: string;
  };
  title?: string;
  thankYouMessage?: string | React.ReactNode;
  children: React.ReactNode;
  withButton?: boolean;
  withBlogButton?: boolean;
  buttonProps?: ButtonProps;
  icon?: 'success' | 'question' | 'warning' | 'finish' | 'badge-check';
  classNameChildren?: string;
  otherButton?: React.ReactNode;
  productId?: number;
}

const AfterDoc: React.FC<Props> = ({
  consultant,
  className,
  title,
  thankYouMessage,
  children,
  withButton = false,
  withBlogButton = false,
  buttonProps,
  icon = 'success',
  classNameChildren,
  otherButton,
  productId = PRODUCT.VEHICLE_DAMAGE,
  ...props
}) => {
  const { i18n } = useLingui();

  return (
    <div
      aria-label={'after-doc'}
      className={cn(
        'w-full max-w-full space-y-4 rounded-[10px] bg-gray-100 px-3.5 py-4 md:space-y-5 md:rounded-lg md:p-6',
        className
      )}
      {...props}
    >
      {title && (
        <h2
          className={cn(
            'flex items-center gap-3 text-blue-600',
            icon !== 'success' && 'mb-0.5'
          )}
        >
          {icon === 'question' && <Question className="shrink-0" />}
          {icon === 'success' && <Flower className="shrink-0" />}
          {icon === 'warning' && <Warning className="shrink-0" />}
          {icon === 'finish' && (
            <Confetti
              width={30}
              height={30}
              className="size-[26px] md:size-[30px]"
            />
          )}
          {icon === 'badge-check' && (
            <BadgeCheck className="size-[26px] shrink-0 md:size-[30px]" />
          )}
          {title}
        </h2>
      )}
      <div className="flex flex-col gap-4 rounded-md bg-gray-200 px-3.5 py-4 text-dark-gray-500 md:p-6 lg:gap-5">
        {consultant && (
          <div className="flex gap-3">
            <Avatar img={consultant.image} name={consultant.name} />
            <div className="flex w-full flex-col justify-center">
              <h5 className="text-dark-gray-500" aria-label="consultant-name">
                {consultant.name}
              </h5>
              <div
                className="body-small mt-0.5 text-dark-gray-300"
                aria-label="job-description"
              >
                {consultant.jobDescription}
              </div>
            </div>
          </div>
        )}
        <div className={cn('body flex flex-col gap-2', classNameChildren)}>
          <h4>{thankYouMessage}</h4>
          {children}
        </div>
        {(withButton || buttonProps) && (
          <div>
            <Button asChild className="mr-auto" {...buttonProps}>
              <a href={`tel:${getHelpfindPhone(productId)}`}>
                <Phone width={18} className="shrink-0" />
                {getHelpfindPhone(productId)}
              </a>
            </Button>
          </div>
        )}
        {withBlogButton && (
          <a className="max-w-fit" href={`https://helpfind.pl/blog`}>
            <Button>
              {i18n.t('Przechodzę na bloga')}
              <ButtonRightArrow />
            </Button>
          </a>
        )}
        {otherButton && otherButton}
      </div>
    </div>
  );
};

export default AfterDoc;
